import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import MotiveRadar from "@/components/MotiveRadar";
import PlayerCard from "@/components/PlayerCard";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { getMotiveIcon, scoreBand } from "@/lib/motives";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  BarChart3,
  Sparkles,
  ClipboardList,
} from "lucide-react";

/**
 * Trainer view of a completed player profile.
 * URL: /players/:playerId
 */
export default function PlayerProfile() {
  const { playerId } = useParams();
  const [data, setData] = useState(null);
  const [motives, setMotives] = useState([]);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, m] = await Promise.all([
          api.get(`/players/${playerId}/profile`),
          api.get("/meta/motives"),
        ]);
        setData(r.data);
        setMotives(m.data.motives);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [playerId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/players/${playerId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FMP_${data.player.name.replace(/ /g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  if (err)
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center">
        <div className="text-red-400">{err}</div>
      </div>
    );
  if (!data)
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center text-white/40">
        Wird geladen …
      </div>
    );

  const { player, team, analysis } = data;
  const { archetype, top_motives, bottom_motives, scores, position_fit } = analysis;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link
          to={team ? `/teams/${team.id}` : "/dashboard"}
          data-testid="profile-back-btn"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#CCFF00] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück
        </Link>
        <button
          data-testid="profile-pdf-btn"
          onClick={downloadPdf}
          disabled={downloading}
          className="btn-primary"
        >
          <Download className="w-5 h-5" />
          {downloading ? "PDF wird erstellt …" : "PDF herunterladen"}
        </button>
      </div>

      <PlayerCard archetype={archetype} player={player} teamName={team?.name} />

      {/* Trainer hints */}
      <div className="card-elevated p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-[#CCFF00]" />
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00]">
            / TRAINERHINWEISE
          </div>
        </div>
        <h3 className="font-heading font-black text-2xl mb-6">
          Handlungsempfehlungen für {archetype.name}
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {archetype.trainer_hints.map((hint, i) => (
            <div
              key={i}
              className="p-5 border border-white/10 bg-[#0f1013] rounded-md flex gap-3"
            >
              <div className="font-heading font-black text-3xl text-[#CCFF00]/70 leading-none">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-sm text-white/80 leading-relaxed pt-1">
                {hint}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Radar + top/bottom motives */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-elevated p-6 md:p-8">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / MOTIV-RADAR
          </div>
          <h3 className="font-heading font-black text-2xl mb-4">
            12 Motive im Überblick
          </h3>
          <MotiveRadar scores={scores} motives={motives} />
        </div>

        <div className="space-y-4">
          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / STÄRKEN
            </div>
            <div className="space-y-2">
              {top_motives.map((m) => {
                const Icon = getMotiveIcon(m.icon);
                return (
                  <MotiveRow key={m.slug} m={m} Icon={Icon} />
                );
              })}
            </div>
          </div>

          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-white/40 mb-3">
              / ENTWICKLUNGS­FELDER
            </div>
            <div className="space-y-2">
              {bottom_motives.map((m) => {
                const Icon = getMotiveIcon(m.icon);
                return <MotiveRow key={m.slug} m={m} Icon={Icon} muted />;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* All motives */}
      <div className="card-elevated p-6 md:p-8">
        <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
          / DETAIL-SCORES
        </div>
        <h3 className="font-heading font-black text-2xl mb-6">
          Alle 12 Motive
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {motives.map((m) => {
            const Icon = getMotiveIcon(m.icon);
            const v = scores[m.slug] || 0;
            const band = scoreBand(v);
            return (
              <div
                key={m.slug}
                data-testid={`profile-motive-${m.slug}`}
                className="p-4 border border-white/10 rounded-md bg-[#0f1013]"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${m.color}22`,
                      border: `1px solid ${m.color}66`,
                      color: m.color,
                    }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold">{m.name}</div>
                    <div
                      className="font-display text-[10px] tracking-widest"
                      style={{ color: band.color }}
                    >
                      {band.label}
                    </div>
                  </div>
                  <div className="font-heading font-black text-xl">{v}</div>
                </div>
                <div className="progress-bar">
                  <div
                    className="fill"
                    style={{
                      width: `${v}%`,
                      background: m.color,
                      boxShadow: `0 0 8px ${m.color}88`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Position fit */}
      <div className="card-elevated p-6 md:p-8">
        <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
          / POSITIONS-PASSUNG
        </div>
        <h3 className="font-heading font-black text-2xl mb-6">
          Empfohlene Positionen
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {position_fit.map((p, i) => (
            <div
              key={p.position}
              className={`p-4 border rounded-md ${
                i === 0
                  ? "border-[#CCFF00]/50 bg-[#CCFF00]/5"
                  : "border-white/10 bg-[#0f1013]"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-heading font-bold text-sm">{p.position}</div>
                <div
                  className="font-heading font-black text-lg"
                  style={{ color: i === 0 ? "#CCFF00" : "#ffffff" }}
                >
                  {p.fit}%
                </div>
              </div>
              <div className="progress-bar">
                <div
                  className="fill"
                  style={{
                    width: `${p.fit}%`,
                    background: i === 0 ? "#CCFF00" : "#94A3B8",
                    boxShadow: i === 0 ? "0 0 8px rgba(204,255,0,0.5)" : "none",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MotiveRow({ m, Icon, muted }) {
  const band = scoreBand(m.score);
  return (
    <div className="flex items-center gap-3 p-2">
      <div
        className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0"
        style={{
          background: muted ? "rgba(255,255,255,0.05)" : `${m.color}22`,
          border: `1px solid ${muted ? "rgba(255,255,255,0.1)" : m.color + "66"}`,
          color: muted ? "#94A3B8" : m.color,
        }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm">{m.name}</div>
        <div
          className="font-display text-[10px] tracking-widest"
          style={{ color: band.color }}
        >
          {band.label}
        </div>
      </div>
      <div className="font-heading font-black text-xl">{m.score}</div>
    </div>
  );
}
